<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">
            <i class="ri-loader-4-line spin"></i>
            儲存中...
          </span>
          <span v-else>
            <i class="ri-save-line"></i>
            儲存
          </span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回</button>
    </div>

    <div v-else class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="active-switch" :class="{ on: form.isActive }">
          <div class="active-switch-copy">
            <span class="active-switch-title">啟用模板</span>
            <span class="active-switch-status">
              {{ form.isActive ? '已啟用，可以發放優惠券' : '已停用，無法再發放新券' }}
            </span>
          </div>
          <label class="active-toggle">
            <input
              v-model="form.isActive"
              type="checkbox"
              aria-label="啟用模板"
            />
            <span class="active-toggle-slider"></span>
          </label>
        </div>

        <div v-if="isEdit" class="template-edit-notice">
          <i class="ri-information-line"></i>
          <div class="template-edit-notice__content">
            <p>修改模板時，已發放優惠券的影響如下：</p>
            <ul>
              <li><strong>有效期限</strong>：不會同步變更（以發放當下為準）</li>
              <li><strong>折扣金額、使用條件、贈品等</strong>：未使用的券將依模板最新內容生效</li>
              <li><strong>停用模板</strong>：無法再發放新券，已發放的券仍可使用</li>
            </ul>
          </div>
        </div>

        <!-- 基本資訊 -->
        <div class="form-section">
          <h2 class="section-title">基本資訊</h2>
          
          <div class="form-group">
            <label class="form-label">模板名稱 <span class="required">*</span></label>
            <input
              v-model="form.templateName"
              type="text"
              class="form-control"
              placeholder="例如：生日優惠券、滿千折百"
              required
              @focus="$event.target.select()"
            />
            <span v-if="errors.templateName" class="error-text">{{ errors.templateName }}</span>
          </div>

          <div class="form-group">
            <label class="form-label">票券圖片</label>
            <ImageUploader
              v-model="form.imageUrl"
              :upload-fn="uploadImageFn"
              shape="square"
              placeholder="上傳票券圖片"
              hint="建議正方形，JPG / PNG / WebP"
              size-hint="建議尺寸 800×800px，最大 5MB"
            />
          </div>

          <div class="form-group">
            <label class="form-label">票券說明</label>
            <textarea
              v-model="form.description"
              class="form-control"
              rows="3"
              placeholder="票券使用說明或注意事項（選填）"
              maxlength="500"
            ></textarea>
            <small class="form-text">顯示在票券詳情頁</small>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">優惠券類型 <span class="required">*</span></label>
              <select v-model="form.couponType" class="form-control" required @change="handleCouponTypeChange">
                <option value="">請選擇類型</option>
                <option value="discount">折扣券（百分比）</option>
                <option value="fixed_amount">折價券（固定金額）</option>
                <option value="free_shipping">運費券</option>
                <option value="gift">贈品券</option>
              </select>
              <span v-if="errors.couponType" class="error-text">{{ errors.couponType }}</span>
            </div>

            <div v-if="form.couponType === 'discount' || form.couponType === 'fixed_amount'" class="form-group">
              <label class="form-label">折扣值 <span class="required">*</span></label>
              <input
                v-model.number="form.discountValue"
                type="number"
                class="form-control"
                :placeholder="form.couponType === 'discount' ? '例如：20（代表 20%）' : '例如：100（代表 $100）'"
                :min="0"
                :step="form.couponType === 'discount' ? '0.1' : '1'"
                required
                @focus="$event.target.select()"
              />
              <span v-if="errors.discountValue" class="error-text">{{ errors.discountValue }}</span>
              <small v-if="form.couponType === 'discount'" class="form-text">
                輸入折扣百分比，例如：填入 <strong>10</strong> 代表打九折（折扣 10%），填入 <strong>20</strong> 代表打八折（折扣 20%）
              </small>
              <small v-else-if="form.couponType === 'fixed_amount'" class="form-text">
                輸入折抵金額，例如：填入 <strong>100</strong> 代表折抵 $100
              </small>
            </div>
          </div>

          <div v-if="form.couponType === 'discount'" class="form-row">
            <div class="form-group">
              <label class="form-label">折扣單位</label>
              <select v-model="form.discountUnit" class="form-control">
                <option value="percent">百分比（%）</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">最高折扣上限</label>
              <input
                v-model.number="form.maxDiscountAmount"
                type="number"
                class="form-control"
                placeholder="例如：500（最多折抵 $500）"
                min="0"
                @focus="$event.target.select()"
              />
              <small class="form-text">不填寫代表無上限</small>
            </div>
          </div>

          <div v-if="form.couponType === 'fixed_amount'" class="form-group">
            <label class="form-label">折扣單位</label>
            <select v-model="form.discountUnit" class="form-control">
              <option value="amount">金額（$）</option>
            </select>
          </div>

          <!-- 贈品券：綁定商品 -->
          <div v-if="form.couponType === 'gift'" class="form-group gift-product-group">
            <label class="form-label">贈品商品 <span class="required">*</span></label>
            <div v-if="giftProductDisplay" class="gift-product-selected">
              <div class="gift-product-info">
                <i class="ri-gift-line"></i>
                <div class="gift-product-text">
                  <span class="gift-product-name">{{ giftProductDisplay.productName }}</span>
                  <span v-if="giftProductDisplay.variantName" class="gift-product-variant">規格：{{ giftProductDisplay.variantName }}</span>
                </div>
              </div>
              <button type="button" class="btn btn-sm btn-secondary" @click="showProductPicker = true">
                <i class="ri-edit-line"></i>
                更換
              </button>
            </div>
            <button v-else type="button" class="btn btn-secondary gift-picker-btn" @click="showProductPicker = true">
              <i class="ri-gift-line"></i>
              選擇贈品商品
            </button>
            <small class="form-text">核銷時將自動扣除此商品規格的庫存</small>
            <span v-if="errors.giftVariantId" class="error-text">{{ errors.giftVariantId }}</span>
          </div>
        </div>

        <!-- 使用條件 -->
        <div class="form-section">
          <h2 class="section-title">使用條件</h2>
          
          <div class="form-group">
            <label class="form-label">最低消費門檻</label>
            <input
              v-model.number="form.minPurchaseAmount"
              type="number"
              class="form-control"
              placeholder="0"
              min="0"
              @focus="$event.target.select()"
            />
            <small class="form-text">最低消費金額，0 代表無門檻</small>
          </div>

          <div class="form-group">
            <label class="form-label">單張預估成本（元）</label>
            <input
              v-model.number="form.estimatedUnitCost"
              type="number"
              class="form-control"
              placeholder="選填，供行銷活動成本預估"
              min="0"
              step="0.01"
              @focus="$event.target.select()"
            />
            <small class="form-text">行銷／財務認定之邊際成本，可與票面折抵不同；不影響核銷與 ROI 實際計算</small>
            <span v-if="errors.estimatedUnitCost" class="error-text">{{ errors.estimatedUnitCost }}</span>
          </div>
        </div>

        <!-- 有效期設定 -->
        <div class="form-section">
          <h2 class="section-title">有效期設定</h2>
          
          <div class="form-group">
            <label class="form-label">有效期類型</label>
            <select v-model="validityType" class="form-control" @change="handleValidityTypeChange">
              <option value="days">發放後天數</option>
              <option value="fixed">固定日期範圍</option>
            </select>
          </div>

          <div v-if="validityType === 'days'" class="form-group">
            <label class="form-label">有效天數</label>
            <input
              v-model.number="form.validityDays"
              type="number"
              class="form-control"
              placeholder="例如：30（發放後 30 天內有效）"
              min="1"
              @focus="$event.target.select()"
            />
            <small class="form-text">從發放日起算的有效天數</small>
          </div>

          <div v-else class="form-row">
            <div class="form-group">
              <label class="form-label">開始日期</label>
              <input
                v-model="form.fixedStartDate"
                type="date"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label class="form-label">結束日期</label>
              <input
                v-model="form.fixedEndDate"
                type="date"
                class="form-control"
              />
            </div>
          </div>
        </div>

        <!-- 發放控制 -->
        <div class="form-section">
          <h2 class="section-title">發放控制</h2>
          
          <div class="form-group">
            <label class="form-label">發行數量總上限</label>
            <input
              v-model.number="form.totalIssueLimit"
              type="number"
              class="form-control"
              placeholder="留空代表不限制"
              min="1"
              @focus="$event.target.select()"
            />
            <small class="form-text">用於控制模板可發出的總票券數量成本（包含分享領取）</small>
          </div>
        </div>

        <!-- 分享與轉送設定 -->
        <div class="form-section">
          <h2 class="section-title">分享與轉送設定</h2>
          
          <div class="form-group">
            <label class="form-label">允許分享</label>
            <div class="toggle-row">
              <label class="toggle-switch">
                <input
                  v-model="form.transferable"
                  type="checkbox"
                />
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-status" :class="form.transferable ? 'on' : 'off'">
                {{ form.transferable ? '已啟用' : '已停用' }}
              </span>
            </div>
            <small class="form-text">會員分享連結給朋友，朋友領取後另發一張新券，會員自己的券仍可使用（會消耗發行數量）</small>
          </div>

          <div class="form-group">
            <label class="form-label">允許轉送</label>
            <div class="toggle-row">
              <label class="toggle-switch">
                <input
                  v-model="form.giftable"
                  type="checkbox"
                />
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-status" :class="form.giftable ? 'on' : 'off'">
                {{ form.giftable ? '已啟用' : '已停用' }}
              </span>
            </div>
            <small class="form-text">會員自己用不到，把整張券送給朋友：連結 24 小時內有效，朋友領取後會員的券變成「已轉出」不可再使用，不會多消耗發行數量</small>
          </div>
        </div>

        <!-- 核銷設定 -->
        <div class="form-section">
          <h2 class="section-title">核銷設定</h2>

          <div class="form-group">
            <div class="radio-group">
              <label class="radio-label">
                <input
                  v-model="form.allowGuestRedemption"
                  type="radio"
                  :value="true"
                  class="form-radio-input"
                />
                <span>允許好友及會員</span>
              </label>
              <label class="radio-label">
                <input
                  v-model="form.allowGuestRedemption"
                  type="radio"
                  :value="false"
                  class="form-radio-input"
                />
                <span>僅限會員</span>
              </label>
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>
  </div>

  <!-- 贈品商品選擇器 -->
  <ProductPickerModal
    :open="showProductPicker"
    title="選擇贈品商品"
    :variant-required="true"
    :show-stock="true"
    @close="showProductPicker = false"
    @select="handleGiftProductSelect"
  />
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { couponService } from '@/api/modules/coupons.js';
import { showSuccess, showError } from '@/utils/swal.js';
import { confirmDeactivateWithReferences } from '@/utils/couponTemplateReferences.js';
import ImageUploader from '@/components/ImageUploader.vue';
import ProductPickerModal from '@/components/ProductPickerModal.vue';

export default {
  name: 'CouponTemplateForm',
  components: { ImageUploader, ProductPickerModal },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const isEdit = computed(() => !!route.params.id);
    const { pageTitle } = usePageTitle(isEdit.value ? '編輯優惠券模板' : '新增優惠券模板');

    const loading = ref(false);
    const submitting = ref(false);
    const error = ref(null);
    const errors = ref({});
    const validityType = ref('days');
    const showProductPicker = ref(false);
    const giftProductDisplay = ref(null);
    const initialIsActive = ref(true);

    const uploadImageFn = (file) => couponService.uploadTemplateImage(file);

    const form = ref({
      templateName: '',
      couponType: '',
      discountValue: null,
      discountUnit: 'percent',
      maxDiscountAmount: null,
      minPurchaseAmount: 0,
      validityDays: 30,
      fixedStartDate: null,
      fixedEndDate: null,
      totalIssueLimit: null,
      transferable: false,
      giftable: false,
      allowGuestRedemption: false,
      requireMemberVerification: true,
      isActive: true,
      imageUrl: null,
      description: null,
      giftProductId: null,
      giftVariantId: null,
      estimatedUnitCost: null
    });

    const fetchTemplate = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      error.value = null;

      try {
        const response = await couponService.getTemplate(route.params.id);

        if (response && response.success && response.data) {
          const template = response.data;
          form.value = {
            templateName: template.templateName || '',
            couponType: template.couponType || '',
            discountValue: template.discountValue || null,
            discountUnit: template.discountUnit || 'percent',
            maxDiscountAmount: template.maxDiscountAmount || null,
            minPurchaseAmount: template.minPurchaseAmount || 0,
            validityDays: template.validityDays || null,
            fixedStartDate: template.fixedStartDate || null,
            fixedEndDate: template.fixedEndDate || null,
            totalIssueLimit: template.totalIssueLimit ?? null,
            transferable: template.transferable || false,
            giftable: template.giftable || false,
            allowGuestRedemption: template.allowGuestRedemption || false,
            requireMemberVerification: template.requireMemberVerification !== false,
            isActive: template.isActive !== false,
            imageUrl: template.imageUrl || null,
            description: template.description || null,
            giftProductId: template.giftProductId || null,
            giftVariantId: template.giftVariantId || null,
            estimatedUnitCost: template.estimatedUnitCost ?? null
          };

          initialIsActive.value = template.isActive !== false;

          // 還原贈品商品顯示資訊（若有綁定）
          if (template.giftProductId && template.giftVariantId) {
            giftProductDisplay.value = {
              productName: template.giftProductName || '已選擇商品',
              variantName: template.giftVariantName || null
            };
          }

          // 設定有效期類型
          if (template.validityDays) {
            validityType.value = 'days';
          } else if (template.fixedStartDate && template.fixedEndDate) {
            validityType.value = 'fixed';
          }
        } else {
          error.value = response?.message || '獲取優惠券模板失敗';
        }
      } catch (err) {
        console.error('獲取優惠券模板失敗:', err);
        error.value = err.message || '獲取優惠券模板失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleCouponTypeChange = () => {
      if (form.value.couponType === 'discount') {
        form.value.discountUnit = 'percent';
      } else if (form.value.couponType === 'fixed_amount') {
        form.value.discountUnit = 'amount';
      } else {
        form.value.discountValue = null;
        form.value.discountUnit = null;
      }
      // 切換類型時清除贈品商品
      if (form.value.couponType !== 'gift') {
        form.value.giftProductId = null;
        form.value.giftVariantId = null;
        giftProductDisplay.value = null;
      }
    };

    const handleGiftProductSelect = ({ product, variant }) => {
      form.value.giftProductId = product.id;
      form.value.giftVariantId = variant ? variant.id : null;
      giftProductDisplay.value = {
        productName: product.productName || product.name,
        variantName: variant ? (variant.variantName || variant.sku) : null
      };
      showProductPicker.value = false;
    };

    const handleValidityTypeChange = () => {
      if (validityType.value === 'days') {
        form.value.fixedStartDate = null;
        form.value.fixedEndDate = null;
        if (!form.value.validityDays) {
          form.value.validityDays = 30;
        }
      } else {
        form.value.validityDays = null;
      }
    };

    const validateForm = () => {
      errors.value = {};
      let isValid = true;

      if (!form.value.templateName || form.value.templateName.trim() === '') {
        errors.value.templateName = '請輸入模板名稱';
        isValid = false;
      }

      if (!form.value.couponType) {
        errors.value.couponType = '請選擇優惠券類型';
        isValid = false;
      }

      if ((form.value.couponType === 'discount' || form.value.couponType === 'fixed_amount') && 
          (form.value.discountValue === null || form.value.discountValue <= 0)) {
        errors.value.discountValue = '請輸入有效的折扣值';
        isValid = false;
      }

      if (form.value.couponType === 'discount' && form.value.discountValue > 100) {
        errors.value.discountValue = '折扣百分比不能超過 100';
        isValid = false;
      }

      if (form.value.couponType === 'gift' && !form.value.giftVariantId) {
        errors.value.giftVariantId = '請選擇贈品商品規格';
        isValid = false;
      }

      if (form.value.estimatedUnitCost != null && form.value.estimatedUnitCost !== '' && form.value.estimatedUnitCost < 0) {
        errors.value.estimatedUnitCost = '預估成本不可小於 0';
        isValid = false;
      }

      if (validityType.value === 'fixed') {
        if (!form.value.fixedStartDate || !form.value.fixedEndDate) {
          showError('請設定完整的有效期日期範圍');
          isValid = false;
        }
      }

      return isValid;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      submitting.value = true;

      try {
        const data = { ...form.value };

        // 根據有效期類型清除不需要的欄位
        if (validityType.value === 'days') {
          data.fixedStartDate = null;
          data.fixedEndDate = null;
        } else {
          data.validityDays = null;
        }

        // 確保日期格式正確，空字串轉為 null
        if (!data.fixedStartDate || data.fixedStartDate === '') {
          data.fixedStartDate = null;
        }
        if (!data.fixedEndDate || data.fixedEndDate === '') {
          data.fixedEndDate = null;
        }

        // 根據優惠券類型處理 discountUnit 和 discountValue
        if (data.couponType === 'free_shipping' || data.couponType === 'gift') {
          // 對於運費券和贈品券，設為 null
          data.discountValue = null;
          data.discountUnit = null;
          data.maxDiscountAmount = null;
        } else {
          // 對於折扣券和折價券，確保 discountUnit 有值
          if (data.couponType === 'discount' && !data.discountUnit) {
            data.discountUnit = 'percent';
          } else if (data.couponType === 'fixed_amount' && !data.discountUnit) {
            data.discountUnit = 'amount';
          }
        }

        if (data.totalIssueLimit === '' || data.totalIssueLimit === undefined) {
          data.totalIssueLimit = null;
        }

        if (data.estimatedUnitCost === '' || data.estimatedUnitCost === undefined) {
          data.estimatedUnitCost = null;
        }

        if (isEdit.value && initialIsActive.value && data.isActive === false) {
          const refResponse = await couponService.getTemplateReferences(route.params.id);
          if (refResponse?.success && refResponse.data) {
            const confirmed = await confirmDeactivateWithReferences(
              refResponse.data.references || [],
              refResponse.data.activeCount || 0
            );
            if (!confirmed) {
              submitting.value = false;
              return;
            }
          }
        }

        let response;
        if (isEdit.value) {
          response = await couponService.updateTemplate(route.params.id, data);
        } else {
          response = await couponService.createTemplate(data);
        }

        if (response && response.success) {
          showSuccess(isEdit.value ? '更新成功' : '建立成功');
          router.push('/client/coupons/templates');
        } else {
          showError(response?.message || (isEdit.value ? '更新失敗' : '建立失敗'));
        }
      } catch (err) {
        console.error('儲存優惠券模板失敗:', err);
        showError(err.message || '儲存失敗');
      } finally {
        submitting.value = false;
      }
    };

    const handleBack = () => {
      router.back();
    };

    onMounted(() => {
      fetchTemplate();
    });

    return {
      loading,
      submitting,
      error,
      errors,
      form,
      validityType,
      isEdit,
      pageTitle,
      uploadImageFn,
      showProductPicker,
      giftProductDisplay,
      handleCouponTypeChange,
      handleGiftProductSelect,
      handleValidityTypeChange,
      handleSubmit,
      handleBack
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_form-common.scss';

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.toggle-status.on {
  color: #4caf50;
}

.toggle-status.off {
  color: #9ca3af;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
}

.form-radio-input {
  cursor: pointer;
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.gift-product-group {
  margin-top: 4px;
}

.gift-picker-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.gift-product-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f0f9f0;
  border: 1px solid #86efac;
  border-radius: 8px;
}

.gift-product-info {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #166534;
}

.gift-product-info i {
  font-size: 20px;
  flex-shrink: 0;
}

.gift-product-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gift-product-name {
  font-weight: 500;
  font-size: 14px;
}

.gift-product-variant {
  font-size: 12px;
  color: #16a34a;
}

.active-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: -16px -16px 20px;
  padding: 16px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 8px 8px 0 0;
}

.active-switch.on {
  background: #f0fdf4;
  border-bottom-color: #bbf7d0;
}

.active-switch-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.active-switch-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.active-switch-status {
  font-size: 13px;
  color: #6b7280;
}

.active-switch.on .active-switch-status {
  color: #15803d;
}

.active-toggle {
  position: relative;
  display: inline-block;
  width: 56px;
  height: 32px;
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
}

.active-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.active-toggle-slider {
  position: absolute;
  inset: 0;
  background: #d1d5db;
  border-radius: 32px;
  transition: background 0.2s;
}

.active-toggle-slider::before {
  content: '';
  position: absolute;
  width: 24px;
  height: 24px;
  left: 4px;
  top: 4px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}

.active-toggle input:checked + .active-toggle-slider {
  background: #16a34a;
}

.active-toggle input:checked + .active-toggle-slider::before {
  transform: translateX(24px);
}

.active-toggle input:focus-visible + .active-toggle-slider {
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.25);
}

.template-edit-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 24px;
  padding: 14px 16px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.6;
}

.template-edit-notice i {
  font-size: 20px;
  color: #d97706;
  flex-shrink: 0;
  margin-top: 1px;
}

.template-edit-notice__content {
  flex: 1;
}

.template-edit-notice__content p {
  margin: 0 0 8px;
}

.template-edit-notice__content ul {
  margin: 0;
  padding-left: 18px;
}

.template-edit-notice__content li + li {
  margin-top: 4px;
}
</style>
